import { getLocale } from '@/i18n'
import { recordActivity } from '@/lib/activity'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function detailFromBody(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const detail = (body as { detail?: unknown }).detail
  if (typeof detail === 'string') return translateApiMessage(detail)
  if (Array.isArray(detail)) {
    const fieldError = detail.find(
      (item) => item && typeof item === 'object' && 'msg' in item,
    ) as { loc?: unknown[]; msg?: string } | undefined
    if (fieldError?.msg) {
      const loc = Array.isArray(fieldError.loc) ? fieldError.loc.map(String).join('.') : ''
      return translateApiMessage(fieldError.msg, loc)
    }
    return detail
      .map((item) => {
        if (typeof item === 'string') return translateApiMessage(item)
        if (item && typeof item === 'object' && 'msg' in item) return translateApiMessage(String((item as { msg: unknown }).msg))
        return JSON.stringify(item)
      })
      .join('; ')
  }
  return undefined
}

function translateApiMessage(message: string, _loc = ''): string {
  if (message.startsWith('Value error, ')) {
    return translateApiMessage(message.slice('Value error, '.length), _loc)
  }
  return message
}

let refreshInFlight: Promise<boolean> | null = null

function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = fetch(`${BASE_URL}/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Accept-Language': getLocale() },
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

export async function apiFetch<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const isForm = init?.body instanceof FormData
  const response = await fetch(`${BASE_URL}/v1${path}`, {
    ...init,
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      'Accept-Language': getLocale(),
      ...init?.headers,
    },
    credentials: 'include',
  })

  const requestId = response.headers.get('x-request-id') ?? undefined

  if (
    response.status === 401 &&
    !retried &&
    path !== '/auth/refresh' &&
    path !== '/auth/login'
  ) {
    const renewed = await refreshSession()
    if (renewed) return apiFetch<T>(path, init, true)
  }

  if (!response.ok) {
    let detail = response.statusText
    try {
      const body: unknown = await response.json()
      detail = detailFromBody(body) ?? detail
    } catch {
      // Тіло не JSON — лишаємо статус як є.
    }
    if (!(response.status === 401 && path.startsWith('/auth/me')) && !path.startsWith('/activity/')) {
      recordActivity({
        kind: 'api_error',
        href: path,
        method: (init?.method as string | undefined) ?? 'GET',
        status: response.status,
        message: detail,
        request_id: requestId ?? null,
      })
    }
    throw new ApiError(response.status, detail, requestId)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export interface HealthResponse {
  status: string
  environment: string
  checks: Record<string, string>
}

export interface UserOut {
  id: string
  email: string
  full_name: string | null
  totp_enabled: boolean
  telegram_user_id: number | null
  locale: string | null
}

export interface OrganizationOut {
  id: string
  name: string
  slug: string
  base_currency_primary: string
  base_currency_secondary: string | null
  timezone: string
  locale: string
  role: string | null
  extraction_mode: 'auto' | 'cloud' | 'local'
}

export interface MeOut {
  user: UserOut
  organization: OrganizationOut
  organizations: OrganizationOut[]
}

export interface CurrencyOut {
  code: string
  kind: string
  name: string
  symbol: string | null
  display_decimals: number
}

export interface ExchangeOut {
  code: string
  name: string
  supports_api: boolean
}

export interface BankOut {
  id: string
  code: string
  name: string
  country: string | null
  aliases: string[]
  is_favorite: boolean
  is_own: boolean
}

export interface AccountOut {
  id: string
  kind: string
  slug: string
  name: string
  exchange_code: string | null
  bank_id: string | null
  currency_code: string | null
  display_hint: string | null
  opening_balance: string
  opening_balance_at: string | null
  is_active: boolean
  include_in_totals: boolean
  has_credentials: boolean
  connector: string | null
  sync_status: string | null
  last_success_at: string | null
  last_error: string | null
  daily_limit: string | null
  daily_used_override: string | null
}

export interface AccountSyncOut {
  skipped: boolean
  ok: boolean | null
  reason: string | null
  stored: number
  duplicates: number
  error: string | null
  connector: string | null
}

export interface OrderOut {
  id: string
  occurred_at: string
  exchange_code: string
  side: 'buy' | 'sell'
  asset_code: string
  quantity: string
  price: string
  fiat_code: string
  fiat_amount: string
  bank_id: string | null
  bank_name: string | null
  payment_method_id: string | null
  account_id: string | null
  counterparty_id: string | null
  counterparty_nickname: string | null
  external_order_id: string | null
  order_status: string
  recon_status: string
  notes: string | null
  tags: string[]
  source: string
  created_at: string
  updated_at: string
}

export interface OrderListOut {
  items: OrderOut[]
  total: number
  limit: number
  offset: number
  active_count?: number
  done_count?: number
  appeal_count?: number
}

export interface FiatBreakdown {
  currency: string
  volume: string
  buy_volume: string
  sell_volume: string
  orders: number
}

export interface OverviewOut {
  from_at: string
  to_at: string
  orders: number
  buy_orders: number
  sell_orders: number
  unmatched: number
  pending_recon: number
  by_fiat: FiatBreakdown[]
  last_order_at: string | null
  last_order_id: string | null
  profit_fifo: string | null
  profit_wac: string | null
  recalculating: string[]
}

export interface SourceFreshness {
  code: string
  label: string
  last_at: string | null
  stale: boolean
  age_seconds: number | null
}

export interface FreshnessOut {
  sources: SourceFreshness[]
  as_of: string
}

export interface RiskFactor {
  code: string
  label: string
  weight: string
  score: string
  detail: string
  severity: string
}

export interface CounterpartyOut {
  id: string
  exchange_code: string
  nickname: string | null
  display_label: string | null
  orders_total: number
  volume_base: string
  last_seen_at: string | null
  risk_level?: string
  risk_score?: string | null
  risk_factors?: RiskFactor[]
  is_watchlisted?: boolean
}

export interface ImportColumnGuess {
  field: string
  column: string | null
  confidence: number
}

export interface ImportPreviewOut {
  exchange_hint: string | null
  delimiter: string
  headers: string[]
  mapping: Record<string, string>
  guesses: ImportColumnGuess[]
  sample: Record<string, unknown>[]
  row_count: number
  issues: string[]
}

export interface ImportCommitOut {
  created: number
  skipped_duplicates: number
  failed: number
  errors: string[]
}

export const getHealth = () => apiFetch<HealthResponse>('/health/ready')

export const login = (email: string, password: string) =>
  apiFetch<MeOut>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })

export const logout = () => apiFetch<{ detail: string }>('/auth/logout', { method: 'POST' })

export const getMe = () => apiFetch<MeOut>('/auth/me')

export const patchMeLocale = (locale: 'uk' | 'ru' | 'en') =>
  apiFetch<MeOut>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify({ locale }),
  })

export const getCurrencies = () => apiFetch<CurrencyOut[]>('/references/currencies')
export const getExchanges = () => apiFetch<ExchangeOut[]>('/references/exchanges')
export const getBanks = (params?: { q?: string; fiat?: string }) => {
  const sp = new URLSearchParams()
  if (params?.q) sp.set('q', params.q)
  if (params?.fiat) sp.set('fiat', params.fiat)
  const suffix = sp.toString() ? `?${sp}` : ''
  return apiFetch<BankOut[]>(`/references/banks${suffix}`)
}
export const createBank = (payload: { name: string; country?: string; aliases?: string[] }) =>
  apiFetch<BankOut>('/references/banks', { method: 'POST', body: JSON.stringify(payload) })
export const toggleBankFavorite = (bankId: string) =>
  apiFetch<BankOut>(`/references/banks/${bankId}/favorite`, { method: 'POST' })

export const getAccounts = () => apiFetch<AccountOut[]>('/accounts')
export const createAccount = (payload: Record<string, unknown>) =>
  apiFetch<AccountOut>('/accounts', { method: 'POST', body: JSON.stringify(payload) })
export const patchAccount = (id: string, payload: Record<string, unknown>) =>
  apiFetch<AccountOut>(`/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
export const deleteAccount = (id: string) =>
  apiFetch<void>(`/accounts/${id}`, { method: 'DELETE' })
export const putAccountCredentials = (id: string, payload: Record<string, unknown>) =>
  apiFetch<AccountOut>(`/accounts/${id}/credentials`, { method: 'PUT', body: JSON.stringify(payload) })
export const deleteAccountCredentials = (id: string) =>
  apiFetch<AccountOut>(`/accounts/${id}/credentials`, { method: 'DELETE' })
export const syncAccount = (id: string) =>
  apiFetch<AccountSyncOut>(`/accounts/${id}/sync`, { method: 'POST' })

export const getOverview = (fromAt?: string, toAt?: string) => {
  const sp = new URLSearchParams()
  if (fromAt) sp.set('from_at', fromAt)
  if (toAt) sp.set('to_at', toAt)
  const suffix = sp.toString() ? `?${sp}` : ''
  return apiFetch<OverviewOut>(`/overview${suffix}`)
}
export const getFreshness = () => apiFetch<FreshnessOut>('/freshness')
export const getOrders = (params?: Record<string, string | undefined>) => {
  const sp = new URLSearchParams()
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value) sp.set(key, value)
    }
  }
  const suffix = sp.toString() ? `?${sp}` : ''
  return apiFetch<OrderListOut>(`/orders${suffix}`)
}
export const getOrder = (id: string) => apiFetch<OrderOut>(`/orders/${id}`)
export const createOrder = (payload: Record<string, unknown>) =>
  apiFetch<OrderOut>('/orders', { method: 'POST', body: JSON.stringify(payload) })
export const patchOrder = (id: string, payload: Record<string, unknown>) =>
  apiFetch<OrderOut>(`/orders/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
export const deleteOrder = (id: string) => apiFetch<void>(`/orders/${id}`, { method: 'DELETE' })
export const getCounterparties = () => apiFetch<CounterpartyOut[]>('/counterparties')

export const previewImport = (file: File) => {
  const body = new FormData()
  body.append('file', file)
  return apiFetch<ImportPreviewOut>('/imports/preview', { method: 'POST', body })
}

export const commitImport = (payload: {
  file: File
  exchange_code: string
  delimiter: string
  mapping: Record<string, string>
  skip_duplicates: boolean
}) => {
  const body = new FormData()
  body.append('file', payload.file)
  body.append('exchange_code', payload.exchange_code)
  body.append('delimiter', payload.delimiter)
  body.append('mapping_json', JSON.stringify(payload.mapping))
  body.append('skip_duplicates', payload.skip_duplicates ? 'true' : 'false')
  return apiFetch<ImportCommitOut>('/imports/commit', { method: 'POST', body })
}

export interface ExtractionFieldOut {
  field_name: string
  value_vlm: string | null
  value_ocr: string | null
  value_final: string | null
  confidence: string | null
  sources_agree: boolean | null
  was_corrected: boolean
  bbox: { x?: number; y?: number; w?: number; h?: number } | null
  row_index: number
}

export interface ExtractionJobOut {
  id: string
  attachment_id: string
  status: 'queued' | 'running' | 'review' | 'committed' | 'rejected' | 'failed'
  detected_exchange: string | null
  detected_screen_type: string
  rows_detected: number
  provider: string | null
  model: string | null
  overall_confidence: string | null
  requires_review: boolean
  validation_flags: { code: string; message: string; severity: string; field_name?: string | null }[]
  corrections_count: number
  error: string | null
  cost_usd: string | null
  created_at: string
  completed_at: string | null
  original_filename: string | null
  private: boolean
  fields: ExtractionFieldOut[]
}

export interface UploadResultOut {
  job: ExtractionJobOut
  duplicate: boolean
}

export interface UsageOut {
  spent_usd: string
  budget_usd: string
  warning: string | null
}

export const uploadScreenshots = (files: File[], isPrivate = false) => {
  const body = new FormData()
  for (const file of files) body.append('files', file)
  body.append('private', isPrivate ? 'true' : 'false')
  return apiFetch<UploadResultOut[]>('/attachments', { method: 'POST', body })
}

export const getExtractionJobs = (status?: string) => {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : ''
  return apiFetch<ExtractionJobOut[]>(`/extraction/jobs${suffix}`)
}

export const getExtractionJob = (id: string) => apiFetch<ExtractionJobOut>(`/extraction/jobs/${id}`)

export const confirmExtraction = (id: string, payload: { row_index?: number; fields: Record<string, string> }) =>
  apiFetch<OrderOut>(`/extraction/jobs/${id}/confirm`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const rejectExtraction = (id: string, reason?: string) =>
  apiFetch<ExtractionJobOut>(`/extraction/jobs/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  })

export const getExtractionUsage = () => apiFetch<UsageOut>('/extraction/usage')

export const getExtractionQueueCount = () =>
  apiFetch<{ review: number; queued: number }>('/extraction/queue-count')

export const bindTelegram = (telegram_user_id: number | null) =>
  apiFetch<MeOut>('/auth/telegram', {
    method: 'PATCH',
    body: JSON.stringify({ telegram_user_id }),
  })

export interface TelegramBotInfo {
  configured: boolean
  username: string | null
}

export const getTelegramBot = () => apiFetch<TelegramBotInfo>('/telegram/bot')

export const patchOrgSettings = (extraction_mode: 'auto' | 'cloud' | 'local') =>
  apiFetch<MeOut>('/auth/org-settings', {
    method: 'PATCH',
    body: JSON.stringify({ extraction_mode }),
  })

export const resetStatistics = (confirm: string) =>
  apiFetch<{ orders: number }>('/analytics/reset-stats', {
    method: 'POST',
    body: JSON.stringify({ confirm }),
  })

export const attachmentFileUrl = (id: string) =>
  `${import.meta.env.VITE_API_BASE_URL ?? '/api'}/v1/attachments/${id}/file`

export interface LotOut {
  id: string
  asset_code: string
  acquired_at: string
  quantity_initial: string
  quantity_remaining: string
  unit_cost: string
  cost_currency: string
  source_order_id: string | null
  is_opening_balance: boolean
  note: string | null
}

export interface PositionOut {
  asset_code: string
  quantity_remaining: string
  open_lots: number
  avg_unit_cost: string | null
  cost_currency: string | null
}

export interface SnapshotOut {
  id: string
  account_id: string
  asset_code: string
  as_of: string
  computed_quantity: string
  actual_quantity: string | null
  drift: string | null
  note: string | null
}

export interface PnLItem {
  id: string
  order_id: string
  occurred_at: string
  currency_code: string
  revenue: string
  fees_total: string
  cost_basis_fifo: string | null
  cost_basis_wac: string | null
  net_profit_fifo: string | null
  net_profit_wac: string | null
  margin_pct_fifo: string | null
  margin_pct_wac: string | null
  net_profit_base_primary: string | null
  net_profit_base_secondary: string | null
  base_primary_code: string | null
  base_secondary_code: string | null
  warnings: string[]
  side: 'buy' | 'sell' | null
  quantity: string | null
  asset_code: string | null
  price: string | null
  exchange_code: string | null
  counterparty_nickname: string | null
  bank_name: string | null
}

export interface PnLSliceOut {
  key: string
  label: string
  sells: number
  revenue_primary: string
  profit_fifo: string
}

export interface PnLDayOut {
  day: string
  sells: number
  revenue_primary: string
  profit_fifo: string
}

export interface PnLSummaryOut {
  from_at: string
  to_at: string
  method: 'fifo' | 'wac'
  currency_code: string | null
  profit_fifo: string
  profit_wac: string
  profit_primary: string | null
  profit_secondary: string | null
  primary_code: string
  secondary_code: string | null
  revenue_primary: string
  fees_primary: string
  avg_margin_pct: string | null
  sells: number
  uncovered_sells: number
  previous_from_at: string | null
  previous_to_at: string | null
  previous_profit_primary: string | null
  previous_sells: number | null
  best_order_id: string | null
  worst_order_id: string | null
  series: PnLDayOut[]
  by_exchange: PnLSliceOut[]
  by_fiat: PnLSliceOut[]
  by_bank: PnLSliceOut[]
  by_counterparty: PnLSliceOut[]
  recalculating: string[]
  items: PnLItem[]
}

export interface FeeOut {
  id: string
  kind: string
  amount: string
  currency_code: string
  note: string | null
}

export const getInventoryLots = (asset?: string) => {
  const suffix = asset ? `?asset=${encodeURIComponent(asset)}` : ''
  return apiFetch<LotOut[]>(`/inventory/lots${suffix}`)
}
export const getInventoryPositions = () => apiFetch<PositionOut[]>('/inventory/positions')
export const getInventorySnapshots = () => apiFetch<SnapshotOut[]>('/inventory/snapshots')
export const createSnapshot = (payload: {
  account_id: string
  asset_code: string
  actual_quantity: string
  note?: string
}) => apiFetch<SnapshotOut>('/inventory/snapshots', { method: 'POST', body: JSON.stringify(payload) })
export const getPnL = (fromAt?: string, toAt?: string, period?: string) => {
  const sp = new URLSearchParams()
  if (period) sp.set('period', period)
  if (fromAt) sp.set('from_at', fromAt)
  if (toAt) sp.set('to_at', toAt)
  const suffix = sp.toString() ? `?${sp}` : ''
  return apiFetch<PnLSummaryOut>(`/pnl${suffix}`)
}
export const getOrderFees = (orderId: string) => apiFetch<FeeOut[]>(`/orders/${orderId}/fees`)
export const addOrderFee = (
  orderId: string,
  payload: { kind: string; amount: string; currency_code: string; note?: string },
) => apiFetch<FeeOut>(`/orders/${orderId}/fees`, { method: 'POST', body: JSON.stringify(payload) })
export const deleteOrderFee = (orderId: string, feeId: string) =>
  apiFetch<void>(`/orders/${orderId}/fees/${feeId}`, { method: 'DELETE' })
export const rebuildInventory = (asset: string) =>
  apiFetch<{ status: string; asset: string }>(`/inventory/rebuild?asset=${encodeURIComponent(asset)}`, {
    method: 'POST',
  })

export interface StatementPreviewOut {
  kind: string
  bank_hint: string | null
  delimiter: string
  headers: string[]
  mapping: Record<string, string>
  signature: string | null
  sample: Record<string, unknown>[]
  row_count: number
  preview_ok: number
  preview_failed: number
  issues: string[]
}

export interface StatementCommitOut {
  created: number
  skipped_duplicates: number
  failed: number
  errors: string[]
  extraction_job_id: string | null
}

export interface BankTransactionOut {
  id: string
  account_id: string
  account_name: string | null
  external_id: string | null
  booked_at: string
  value_date: string | null
  direction: 'in' | 'out'
  amount: string
  currency_code: string
  balance_after: string | null
  payer_name: string | null
  payer_account: string | null
  payer_display_hint: string | null
  description: string | null
  reference: string | null
  recon_status: string
  allocated_amount: string
  is_reversal: boolean
  source: string
  created_at: string
}

export interface BankTransactionListOut {
  items: BankTransactionOut[]
  total: number
  limit: number
  offset: number
}

export const previewStatement = (file: File) => {
  const body = new FormData()
  body.append('file', file)
  return apiFetch<StatementPreviewOut>('/statements/preview', { method: 'POST', body })
}

export const commitStatement = (payload: {
  file: File
  account_id: string
  delimiter: string
  mapping: Record<string, string>
  skip_duplicates?: boolean
}) => {
  const body = new FormData()
  body.append('file', payload.file)
  body.append('account_id', payload.account_id)
  body.append('delimiter', payload.delimiter)
  body.append('mapping_json', JSON.stringify(payload.mapping))
  body.append('skip_duplicates', payload.skip_duplicates === false ? 'false' : 'true')
  return apiFetch<StatementCommitOut>('/statements/commit', { method: 'POST', body })
}

export const commitStatementFromExtraction = (
  jobId: string,
  payload: { account_id: string; rows?: Record<string, string>[] },
) =>
  apiFetch<StatementCommitOut>(`/statements/from-extraction/${jobId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const getTransactions = (params?: Record<string, string | undefined>) => {
  const sp = new URLSearchParams()
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value) sp.set(key, value)
    }
  }
  const suffix = sp.toString() ? `?${sp}` : ''
  return apiFetch<BankTransactionListOut>(`/transactions${suffix}`)
}

export const getTransaction = (id: string) => apiFetch<BankTransactionOut>(`/transactions/${id}`)

export interface ReconOrderOut {
  id: string
  occurred_at: string
  side: 'buy' | 'sell'
  exchange_code: string
  asset_code: string
  quantity: string
  fiat_code: string
  fiat_amount: string
  counterparty: string | null
  bank_name: string | null
  recon_status: string
  external_order_id: string | null
}

export interface ReconTxOut {
  id: string
  booked_at: string
  direction: 'in' | 'out'
  amount: string
  currency_code: string
  account_name: string | null
  payer_hint: string | null
  description: string | null
  recon_status: string
  unallocated: string
}

export interface ReconFactor {
  code: string
  score: string
  weight: string
  contribution: string
  explanation: string
  details?: Record<string, unknown>
}

export interface MatchOut {
  id: string
  status: string
  match_type: string
  method: string
  score: string
  factors: { total?: string; engine_version?: string; factors?: ReconFactor[] }
  matched_amount: string | null
  currency_code: string | null
  residual_amount: string
  residual_explained_as: string | null
  engine_version: string | null
  orders: ReconOrderOut[]
  transactions: ReconTxOut[]
  created_at: string
}

export interface ExceptionOut {
  id: string
  kind: string
  severity: string
  status: string
  title: string
  entity_type: string
  entity_id: string
  impact_amount: string | null
  impact_currency: string | null
  details: Record<string, unknown>
  detected_at: string
}

export interface CandidateOut {
  order_id: string
  bank_transaction_id: string
  score: string
  time_delta_seconds: number | null
  amount_delta: string | null
  factors: Record<string, unknown>
}

export interface ReconBoardOut {
  unmatched_orders: ReconOrderOut[]
  unmatched_transactions: ReconTxOut[]
  proposed: MatchOut[]
  confirmed: MatchOut[]
  exceptions: ExceptionOut[]
  candidates: CandidateOut[]
  auto_matched: number
  pending_review: number
  open_exceptions: number
}

export interface ReconRunOut {
  auto_matched: number
  proposed: number
  candidates: number
  exceptions: number
  orders_seen: number
  transactions_seen: number
}

export const getReconBoard = () => apiFetch<ReconBoardOut>('/reconciliation')
export const runReconciliation = () =>
  apiFetch<ReconRunOut>('/reconciliation/run', { method: 'POST' })
export const confirmMatch = (id: string) =>
  apiFetch<MatchOut>(`/reconciliation/matches/${id}/confirm`, { method: 'POST' })
export const rejectMatch = (id: string) =>
  apiFetch<MatchOut>(`/reconciliation/matches/${id}/reject`, { method: 'POST' })
export const confirmManualMatch = (order_ids: string[], transaction_ids: string[]) =>
  apiFetch<MatchOut>('/reconciliation/matches', {
    method: 'POST',
    body: JSON.stringify({ order_ids, transaction_ids }),
  })
export const bulkConfirmMatches = (ids: string[]) =>
  apiFetch<{ confirmed: number }>('/reconciliation/matches/bulk-confirm', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
export const bulkRejectMatches = (ids: string[]) =>
  apiFetch<{ rejected: number }>('/reconciliation/matches/bulk-reject', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
export const resolveException = (id: string, note?: string) =>
  apiFetch<ExceptionOut>(`/reconciliation/exceptions/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ note: note ?? null }),
  })

export interface SliceOut {
  key: string
  label: string
  orders: number
  buy_orders: number
  sell_orders: number
  volume_primary: string
  profit_fifo: string
  unmatched: number
  last_buy_price: string | null
  last_sell_price: string | null
  last_buy_fiat: string | null
  last_sell_fiat: string | null
}

export interface DayPointOut {
  day: string
  orders: number
  volume_primary: string
  profit_fifo: string
}

export interface PreviousPeriodOut {
  from_at: string
  to_at: string
  orders: number
  volume_primary: string
  profit_primary: string
  spread_pct: string | null
  qty_bought: string
  qty_sold: string
}

export interface DashboardOut {
  from_at: string
  to_at: string
  primary_code: string
  secondary_code: string | null
  orders: number
  buy_orders: number
  sell_orders: number
  volume_primary: string
  profit_fifo: string
  profit_wac: string
  profit_primary: string
  unmatched: number
  pending_recon: number
  matched: number
  open_exceptions: number
  confirmed_matches: number
  asset_code: string | null
  qty_bought: string
  qty_sold: string
  vwap_buy: string | null
  vwap_sell: string | null
  spread_pct: string | null
  fees_primary: string
  last_buy_price: string | null
  last_sell_price: string | null
  last_buy_exchange: string | null
  last_sell_exchange: string | null
  last_buy_fiat: string | null
  last_sell_fiat: string | null
  previous: PreviousPeriodOut | null
  series: DayPointOut[]
  by_exchange: SliceOut[]
  by_bank: SliceOut[]
  by_asset: SliceOut[]
  by_payment_method: SliceOut[]
  by_hour: SliceOut[]
  by_counterparty: SliceOut[]
  limits: BankLimitOut[]
}

export interface BankLimitOut {
  account_id: string
  name: string
  currency_code: string | null
  limit: string
  used_auto: string
  used_final: string
  remaining: string
  pct: string
  override: boolean
  warn: boolean
}

export interface FxRateOut {
  rate_date: string
  base_code: string
  quote_code: string
  rate: string
  source: string
}

export const getFxRates = (on?: string) => {
  const suffix = on ? `?on=${encodeURIComponent(on)}` : ''
  return apiFetch<FxRateOut[]>(`/fx/rates${suffix}`)
}

export interface SpotQuoteOut {
  pair: string
  last: string
  bid: string
  ask: string
  mid: string
  volume_24h: string
  source: string
  fetched_at: string
  kind: 'spot'
  stale: boolean
}

export interface SpotMarketOut {
  quotes: SpotQuoteOut[]
  error: string | null
  stale: boolean
}

export const getMarketSpot = () => apiFetch<SpotMarketOut>('/market/spot')

export interface HistoryPointOut {
  at: string
  kind: 'spot' | 'fx' | 'our_p2p'
  pair: string
  buy: string | null
  sell: string | null
  mid: string | null
  source: string | null
}

export interface MarketHistoryOut {
  kind: 'spot' | 'fx' | 'our_p2p'
  pair: string | null
  bucket: string
  points: HistoryPointOut[]
}

export const getMarketHistory = (opts: {
  kind: 'spot' | 'fx' | 'our_p2p'
  pair?: string
  fromAt?: string
  toAt?: string
  bucket?: 'hour' | 'day'
}) => {
  const sp = new URLSearchParams({ kind: opts.kind })
  if (opts.pair) sp.set('pair', opts.pair)
  if (opts.fromAt) sp.set('from_at', opts.fromAt)
  if (opts.toAt) sp.set('to_at', opts.toAt)
  if (opts.bucket) sp.set('bucket', opts.bucket)
  return apiFetch<MarketHistoryOut>(`/market/history?${sp.toString()}`)
}

export const getDashboard = (opts?: { period?: string; fromDate?: string; toDate?: string; fromAt?: string; toAt?: string }) => {
  const sp = new URLSearchParams()
  if (opts?.period) sp.set('period', opts.period)
  if (opts?.fromDate) sp.set('from_date', opts.fromDate)
  if (opts?.toDate) sp.set('to_date', opts.toDate)
  if (opts?.fromAt) sp.set('from_at', opts.fromAt)
  if (opts?.toAt) sp.set('to_at', opts.toAt)
  const suffix = sp.toString() ? `?${sp}` : ''
  return apiFetch<DashboardOut>(`/analytics/dashboard${suffix}`)
}

export interface ReportDefinitionOut {
  id: string
  slug: string
  name: string
  kind: string
  description: string | null
  params: Record<string, unknown>
  default_format: 'csv' | 'xlsx' | 'pdf' | 'json'
  created_at: string
}

export interface ReportRunOut {
  id: string
  definition_id: string | null
  kind: string
  format: 'csv' | 'xlsx' | 'pdf' | 'json'
  period_from: string | null
  period_to: string | null
  params: Record<string, unknown>
  status: string
  size_bytes: number | null
  row_count: number | null
  engine_version: string | null
  error: string | null
  created_at: string
  completed_at: string | null
  download_path: string | null
}

export const getReportDefinitions = () => apiFetch<ReportDefinitionOut[]>('/reports/definitions')
export const createReportDefinition = (payload: {
  name: string
  kind: string
  description?: string
  params?: Record<string, unknown>
  default_format?: string
}) => apiFetch<ReportDefinitionOut>('/reports/definitions', { method: 'POST', body: JSON.stringify(payload) })
export const getReportRuns = () => apiFetch<ReportRunOut[]>('/reports/runs')
export const runReport = (payload: Record<string, unknown>) =>
  apiFetch<ReportRunOut>('/reports/run', { method: 'POST', body: JSON.stringify(payload) })
export const runMonthlyReport = (year: number, month: number, format: 'xlsx' | 'pdf' | 'csv' = 'xlsx') =>
  apiFetch<ReportRunOut>('/reports/monthly', {
    method: 'POST',
    body: JSON.stringify({ year, month, format }),
  })
export const reportFileUrl = (id: string) =>
  `${import.meta.env.VITE_API_BASE_URL ?? '/api'}/v1/reports/runs/${id}/file`

export interface RiskRuleOut {
  id: string
  code: string
  name: string
  description: string | null
  enabled: boolean
  severity: string
  weight: string
  params: Record<string, unknown>
  notify: boolean
  last_triggered_at: string | null
  triggered_count: number
}

export interface RiskEventOut {
  id: string
  rule_code: string
  entity_type: string
  entity_id: string
  counterparty_id: string | null
  severity: string
  score: string | null
  factors: RiskFactor[]
  title: string
  details: Record<string, unknown>
  impact_amount: string | null
  impact_currency: string | null
  status: string
  detected_at: string
  resolution_note: string | null
}

export interface AlertOut {
  id: string
  risk_event_id: string | null
  kind: string
  severity: string
  title: string
  body: string | null
  payload: Record<string, unknown>
  status: string
  delivered_channels: string[]
  sent_at: string | null
  read_at: string | null
  snoozed_until: string | null
  delivery_error: string | null
  created_at: string
}

export interface NotificationChannelOut {
  id: string
  kind: string
  name: string
  is_active: boolean
  min_severity: string
  quiet_hours: { from?: string; to?: string } | null
  last_success_at: string | null
  last_error: string | null
  consecutive_failures: number
  config: Record<string, unknown>
}

export interface CounterpartyProfileOut extends CounterpartyOut {
  orders_completed: number
  orders_cancelled: number
  first_seen_at: string | null
  notes: string | null
  events: RiskEventOut[]
  recent_orders: {
    id: string
    occurred_at: string
    side: string
    fiat_amount: string
    fiat_code: string
    recon_status: string
    external_order_id: string | null
  }[]
}

export const getRiskRules = () => apiFetch<RiskRuleOut[]>('/risk/rules')
export const patchRiskRule = (id: string, payload: Record<string, unknown>) =>
  apiFetch<RiskRuleOut>(`/risk/rules/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
export const getRiskEvents = () => apiFetch<RiskEventOut[]>('/risk/events')
export const ackRiskEvent = (id: string, note?: string) =>
  apiFetch<RiskEventOut>(`/risk/events/${id}/ack`, { method: 'POST', body: JSON.stringify({ note }) })
export const dismissRiskEvent = (id: string, note?: string) =>
  apiFetch<RiskEventOut>(`/risk/events/${id}/dismiss`, { method: 'POST', body: JSON.stringify({ note }) })
export const evaluateRisk = (trigger = 'manual') =>
  apiFetch<{ events: number; alerts: number; unmatched_released: number }>('/risk/evaluate', {
    method: 'POST',
    body: JSON.stringify({ trigger }),
  })
export const getAlerts = () => apiFetch<AlertOut[]>('/alerts')
export const getAlertsUnreadCount = () => apiFetch<{ count: number }>('/alerts/unread-count')
export const markAlertRead = (id: string) =>
  apiFetch<AlertOut>(`/alerts/${id}/read`, { method: 'POST' })
export const snoozeAlert = (id: string, minutes = 60) =>
  apiFetch<AlertOut>(`/alerts/${id}/snooze`, { method: 'POST', body: JSON.stringify({ minutes }) })
export const getNotificationChannels = () => apiFetch<NotificationChannelOut[]>('/notification-channels')
export const patchNotificationChannel = (id: string, payload: Record<string, unknown>) =>
  apiFetch<NotificationChannelOut>(`/notification-channels/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
export const getCounterparty = (id: string) => apiFetch<CounterpartyProfileOut>(`/counterparties/${id}`)
export const patchCounterparty = (id: string, payload: { is_watchlisted?: boolean; notes?: string }) =>
  apiFetch<CounterpartyProfileOut>(`/counterparties/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })

export interface ActivityOut {
  id: string
  created_at: string
  actor_label: string
  action: string
  after: Record<string, unknown> | null
  request_id: string | null
}

export const getActivityEvents = (limit = 150, errorsOnly = false) => {
  const params = new URLSearchParams({ limit: String(limit) })
  if (errorsOnly) params.set('errors_only', 'true')
  return apiFetch<ActivityOut[]>(`/activity/events?${params.toString()}`)
}

